import type { CSSProperties, ReactNode } from 'react';
import { SPAN, tiles } from './world';
import type { SlideSpec } from './slides';

interface Props {
  slides: SlideSpec[];
  /** The stop the train is at, or has just left. */
  index: number;
  /** Anything in the sky, drawn behind the city (the last ride's fireworks). */
  sky?: ReactNode;
}

/**
 * How each stop's platform is built, by its place on the line: the central
 * station's steel shed, then a run of different small stations so no two
 * stops in a row look alike.
 */
const STYLES = ['central', 'wooden', 'glass', 'concrete', 'country', 'tiled'] as const;
export const platformStyle = (i: number) => (i === 0 ? 'central' : STYLES[1 + ((i - 1) % (STYLES.length - 1))]!);

/**
 * Everything outside the window. Each layer is positioned from `--cx`, the
 * camera's distance along the line, scaled by how far away the layer is: far
 * things barely move, the poles by the track flash past. A platform sits at
 * each stop's position along the line, so arriving at a station is the sign
 * and platform really sliding into view and the camera really coming to rest
 * in front of them.
 *
 * The sky is on the same clock: `--p` is how far along the whole ride the
 * camera is, so the train leaves at dusk and reaches the last stop at
 * midnight, the sun setting, the stars and moon coming out and the windows
 * lighting up as it goes.
 */
export function World({ slides, index, sky }: Props) {
  const layer = (k: number, bg?: string): CSSProperties =>
    ({ ['--k' as string]: k, ...(bg ? { backgroundImage: bg } : {}) }) as CSSProperties;

  return (
    <div className="tw" aria-hidden="true">
      <div className="sky" />
      <div className="sky eve" />
      <div className="sky dusk" />
      <div className="stars" />
      <div className="stars faint" />
      <div className="sun" />
      <div className="moon" />
      {sky}
      <div className="tly skyline" style={layer(0.05, tiles.skyline)} />
      <div className="tly skyline lit" style={layer(0.05, tiles.skylineLit)} />
      <div className="haze" />
      <div className="tly hills" style={layer(0.14, tiles.hills)} />
      <div className="tground" />
      <div className="tly town" style={layer(0.4, tiles.town)} />
      <div className="tly town lit" style={layer(0.4, tiles.townLit)} />
      <div className="tly bed" style={layer(1)} />

      {slides.map((s, i) =>
        Math.abs(i - index) <= 1 ? (
          <div
            key={s.kind}
            className={`plat ps-${platformStyle(i)}`}
            style={{ ['--at' as string]: `${i * SPAN}px` } as CSSProperties}
          >
            <div className="canopy" />
            <div className="posts" />
            <div className="slab" />
            <div className="sign">
              <b>{s.sign.kanji}</b>
              <span>{s.sign.en}</span>
            </div>
            <i className="tlamp l1" />
            <i className="tlamp l2" />
            <i className="tlamp l3" />
            <div className="bench" />
            <div className="vend" />
            <div className="prop p1" />
            <div className="prop p2" />
          </div>
        ) : null,
      )}

      <div className="tly poles" style={layer(1.6, tiles.poles)} />
      <div className="speed" />
    </div>
  );
}
